// ts-rest contract for the upload history of the Uploads page of the Admin
// shell.
//
// The history lists each upload that an administrator made on that page, with
// its files. The page marks each upload request, and the server keeps the
// mark on each file (`upload-surface.ts`). An upload of a door that keeps no
// bytes (the two KYC doors, ADR-0009) has a line with no file to download.
//
// Endpoints:
//   GET /api/admin/upload-history                     — the uploads, newest first
//   GET /api/admin/upload-history/files/:id/content   — the bytes of one file
//
// The download route is a plain binary route of the controller, and not a
// ts-rest entry, because it answers with file bytes. The controller gates both
// on the admin persona (ADR-0004), and it audits each download.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

export const uploadHistoryFileSchema = z.object({
    /** The source upload id of the file. */
    id: z.string(),
    filename: z.string(),
    byteSize: z.number().int().nonnegative().nullable(),
    /** False when the server keeps no bytes for the file. */
    available: z.boolean(),
});
export type UploadHistoryFile = z.infer<typeof uploadHistoryFileSchema>;

export const uploadHistoryRowSchema = z.object({
    /** The id of the first file of the upload. */
    id: z.string(),
    /** The name of the upload on the page, or null for an upload with no name. */
    label: z.string().nullable(),
    /** The door key that took the file in, for example `rbc_valuation`. A value. */
    door: z.string().nullable(),
    uploadedAt: z.string(),
    uploadedByName: z.string().nullable(),
    files: z.array(uploadHistoryFileSchema),
    /** The ingest plan of the upload, for a door that prepares a plan. */
    plan: z
        .object({
            id: z.string(),
            status: z.enum(['draft', 'applied', 'discarded', 'expired']),
        })
        .nullable(),
});
export type UploadHistoryRow = z.infer<typeof uploadHistoryRowSchema>;

export const uploadHistoryResponseSchema = z.object({
    rows: z.array(uploadHistoryRowSchema),
    /** The number of uploads that match, over all pages. */
    total: z.number().int().nonnegative(),
});
export type UploadHistoryResponse = z.infer<typeof uploadHistoryResponseSchema>;

export const adminUploadHistoryContract = c.router(
    {
        list: {
            method: 'GET',
            path: '/admin/upload-history',
            query: z.object({
                /** Matches the name of the upload or the name of a file. */
                search: z.string().optional(),
                page: z.coerce.number().int().min(1).optional(),
                pageSize: z.coerce.number().int().min(1).max(100).optional(),
            }),
            responses: { 200: uploadHistoryResponseSchema },
            summary: 'Admin: the uploads of the Admin Uploads page, newest first.',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
